/**
 * WHO IS INSIDE A SHAPE.
 *
 * The battlefield has drawn a blast ring since v7.16.0 and nothing has ever
 * consumed it - `BlastOverlay` counts names for a label and stops there. These
 * two functions are what turn the drawing into an attack: given a shape and
 * the tokens on the map, which combatants does it catch?
 *
 * IDS ONLY, per the `sweptIds` precedent (`deployment.ts`). Everything an area
 * attack then needs to know about each victim - their cover, their range band,
 * their hit location, their armour - is a fact about the PAIR (attacker,
 * victim), not about the shape, and `relateFrom` already answers it. Returning
 * anything richer here would invite a caller to treat the shape's opinion as
 * the victim's, which is exactly the mistake the v7.51.0 cover rewrite undid.
 *
 * PURE, and it stores nothing.
 *
 * Three rules are worth stating plainly, because each of them is a decision
 * rather than a consequence:
 *
 *   1. THE FOOT DECIDES, not the footprint - one body, one point, like every
 *      other region query on this map. A horde is the exception, because a
 *      horde IS an area (see `hordeRadius`).
 *   2. THE EDGE IS INSIDE. `sweptIds` and the deployment box are both
 *      inclusive, and a ring that visibly touches a token but does not hurt
 *      them reads as a bug.
 *   3. THE THROWER IS NOT IN THE SHAPE THEY AIMED. A melee blast is centred on
 *      somebody you are standing next to, so its radius covers you every time;
 *      the app would otherwise have you grenade yourself on every swing.
 *      Intent decides - and scatter, which is the failure of intent, is what
 *      puts you back inside it. Callers pass the attacker in `exclude`.
 */
import { blocksLineOfSight } from './lineOfSight';
import { tokenEye, hordeRadius } from './tokens';
import { bearingBetween } from './facing';
import type { Token } from './tokens';
import type { Solid, Vec3 } from './types';

/** Where a shape is centred. Elevation defaults to the ground. */
export interface AreaOrigin {
  x: number;
  z: number;
  elevation?: number;
}

export interface AreaOptions {
  /** Terrain, for the sight gate. Omit for an unobstructed field. */
  solids?: Solid[];
  /** GM clients see terrain players are not shown. */
  includeHidden?: boolean;
  /**
   * Must the burst SEE a combatant to catch them? Default true.
   *
   * False is for a caller that has already decided otherwise - a shape drawn
   * by hand, or a ruling that a blast fills a room.
   */
  requireSight?: boolean;
  /** Magnitude per combatant id, for the hordes that are areas themselves. */
  hordeMagnitude?: Record<string, number>;
  /** Ids that this shape cannot catch - above all, the attacker. */
  exclude?: readonly string[] | Set<string>;
  /**
   * Who the template CATCHES at all (v8.372.2): false and they stand inside
   * it untouched - a squad-only buff-blast, a hostiles-only cone. The block's
   * `catches` becomes this predicate at the simulator, by team tag.
   */
  eligible?: (id: string) => boolean;
}

/**
 * How high above the centre the burst is reckoned to happen.
 *
 * Sight is traced from here rather than from the dirt, because a detonation
 * at ground level is blocked by the first kerb between it and everyone, and
 * that is not how a grenade behaves. One metre is roughly the height a
 * thrown charge goes off at and matches the eye of a prone target.
 */
const BURST_HEIGHT_M = 1;

const DEG = Math.PI / 180;

function toSet(ids: AreaOptions['exclude']): Set<string> {
  if (!ids) return new Set();
  return ids instanceof Set ? ids : new Set(ids);
}

/** Present, not excluded, and not ruled out by the caller's own test. */
function eligibleToken(token: Token | undefined, excluded: Set<string>, options: AreaOptions): token is Token {
  if (!token || excluded.has(token.id)) return false;
  return options.eligible?.(token.id) !== false;
}

function burstPoint(origin: AreaOrigin): Vec3 {
  return { x: origin.x, y: (origin.elevation ?? 0) + BURST_HEIGHT_M, z: origin.z };
}

/** How much of a disc a combatant occupies. Zero for one body. */
function bulkOf(token: Token, hordeMagnitude: AreaOptions['hordeMagnitude']): number {
  const magnitude = hordeMagnitude?.[token.id];
  return magnitude ? hordeRadius(magnitude, token.footprint) : 0;
}

/** Straight-line distance from a burst to a combatant's feet, in metres. */
function distanceFrom(origin: AreaOrigin, token: Token): number {
  return Math.hypot(token.x - origin.x, token.z - origin.z, token.elevation - (origin.elevation ?? 0));
}

/**
 * Can the burst reach them?
 *
 * OPAQUE terrain only. Half cover is not a roof: it reduces the damage - which
 * happens per attacker, elsewhere - but it does not put a combatant outside
 * the blast. A token that ignores terrain is never sheltered by it, at either
 * end, exactly as in `relations.ts`.
 */
function visibleFromBurst(origin: AreaOrigin, token: Token, options: AreaOptions): boolean {
  if (options.requireSight === false) return true;
  const solids = options.solids;
  if (!solids?.length || token.ignoresTerrain) return true;
  return !blocksLineOfSight(burstPoint(origin), tokenEye(token), solids, {
    includeHidden: options.includeHidden ?? false,
  });
}

/**
 * Everyone a circular blast catches.
 *
 * Distance is measured in three dimensions, so a charge on the floor does not
 * reach the gantry directly above it.
 */
export function combatantsInCircle(
  origin: AreaOrigin,
  radius: number,
  tokens: readonly Token[],
  options: AreaOptions = {},
): string[] {
  if (!(radius > 0)) return [];
  const excluded = toSet(options.exclude);

  return tokens
    .filter((token) => {
      if (!eligibleToken(token, excluded, options)) return false;
      if (distanceFrom(origin, token) > radius + bulkOf(token, options.hordeMagnitude)) return false;
      return visibleFromBurst(origin, token, options);
    })
    .map((token) => token.id);
}

/** The smaller of the two ways round the circle, in degrees. */
function angularGap(a: number, b: number): number {
  const raw = Math.abs(((a - b) % 360) + 360) % 360;
  return raw > 180 ? 360 - raw : raw;
}

/**
 * Everyone a cone catches - a flamer, and anything else `Spray(X)` describes.
 *
 * `degrees` is the TOTAL included angle, so the cone reaches half of it either
 * side of `bearing`. Reading it as a half-angle would silently double every
 * flamer in the game, which is why the test says so out loud.
 *
 * THE SHOOTER IS EXCLUDED BY ID, by the caller - you are not in your own
 * flamer. It cannot be done by position: at a table where nobody has deployed
 * yet, every token shares one spot, and excluding "whoever stands at the
 * apex" threw away every victim there was.
 */
/**
 * Beam (v8.292.0): everyone standing ON the firing line, from the
 * muzzle out to `length`. The lance idiom of `combatantsInCone`,
 * with the angular test swapped for a perpendicular one: axial
 * distance in [0, length], lateral distance within `halfWidthM`
 * plus the body's own bulk reaching into the ray.
 */
export function combatantsAlongBeam(
  apex: AreaOrigin,
  bearing: number,
  length: number,
  tokens: readonly Token[],
  options: AreaOptions = {},
  halfWidthM = 1,
): string[] {
  if (!(length > 0)) return [];
  const excluded = toSet(options.exclude);
  const along = { x: Math.cos(bearing * DEG), z: Math.sin(bearing * DEG) };

  return tokens
    .filter((token) => {
      if (!eligibleToken(token, excluded, options)) return false;
      const bulk = bulkOf(token, options.hordeMagnitude);
      const dx = token.x - apex.x;
      const dz = token.z - apex.z;
      const axial = dx * along.x + dz * along.z;
      // Behind the muzzle, or past the beam's reach (bulk leans in).
      if (axial < -bulk || axial > length + bulk) return false;
      const lateral = Math.abs(dx * along.z - dz * along.x);
      if (lateral > halfWidthM + bulk) return false;
      return visibleFromBurst(apex, token, options);
    })
    .map((token) => token.id);
}

export function combatantsInCone(
  apex: AreaOrigin,
  bearing: number,
  length: number,
  degrees: number,
  tokens: readonly Token[],
  options: AreaOptions = {},
): string[] {
  if (!(length > 0) || !(degrees > 0)) return [];
  const excluded = toSet(options.exclude);
  const cone = { bearing, length, halfAngle: Math.min(180, degrees / 2) };
  return tokens
    .filter((token) => eligibleToken(token, excluded, options) && coneCatches(apex, token, cone, options))
    .map((token) => token.id);
}

/**
 * Is this combatant inside the cone, bulk and all, and reachable by it?
 *
 * STANDING EXACTLY WHERE THE SHOOTER STANDS. This used to be read as
 * "the one holding the flamer" and excluded, on the grounds that you are
 * not in your own cone. But the shooter is excluded BY ID by the caller,
 * so everyone left here at zero distance is somebody SHARING their spot -
 * which is the normal state of a roster nobody has deployed yet, where
 * every token sits on the origin. The cone then caught nobody at all,
 * silently, including the person it was pointed at (2026-08-18). At zero
 * distance there is no bearing to compare, and somebody at the muzzle is
 * in the flame under any reading of it.
 *
 * A wide body reaches into the cone from outside its edge. The angle it
 * subtends at the apex is asin(bulk / distance), which is undefined once
 * the apex is inside the disc - and then it is caught outright.
 */
function coneCatches(
  apex: AreaOrigin,
  token: Token,
  cone: { bearing: number; length: number; halfAngle: number },
  options: AreaOptions,
): boolean {
  const bulk = bulkOf(token, options.hordeMagnitude);
  const distance = distanceFrom(apex, token);
  if (distance > cone.length + bulk) return false;
  if (distance <= 1e-6 || bulk >= distance) return visibleFromBurst(apex, token, options);
  const spread = bulk > 0 ? Math.asin(bulk / distance) / DEG : 0;
  if (angularGap(bearingBetween(apex, token), cone.bearing) > cone.halfAngle + spread) return false;
  return visibleFromBurst(apex, token, options);
}

/** One link of an arc: who, and how many leaps from the primary (0 = the primary). */
export interface ArcLeap {
  id: string;
  leap: number;
}

/**
 * The chain an arc walks (v8.372.2). From the primary victim, each leap goes
 * to the NEAREST untouched eligible combatant within `reach` metres of the
 * LAST one - never back to somebody already struck, never to anyone the
 * caller excludes (the attacker above all) - and stops at the first leap
 * that finds nobody. Ties break on the smaller id so the result does not
 * depend on the order the tokens arrive in. `sightPerLeap` traces the
 * burst's sight test from the last victim; off, a wall between two victims
 * does not stop the lightning (what Arcing always did).
 */
export function arcChain(
  primary: Token,
  tokens: readonly Token[],
  reach: number,
  leaps: number,
  options: AreaOptions & { sightPerLeap?: boolean } = {},
): ArcLeap[] {
  const chain: ArcLeap[] = [{ id: primary.id, leap: 0 }];
  if (!(reach > 0) || !(leaps > 0)) return chain;
  const touched = new Set<string>([primary.id, ...toSet(options.exclude)]);
  const sight: AreaOptions = { ...options, requireSight: options.sightPerLeap === true };
  let current: Token = primary;
  for (let leap = 1; leap <= leaps; leap++) {
    const next = nearestLeap(current, tokens, { reach, touched, options, sight });
    if (!next) break;
    chain.push({ id: next.id, leap });
    touched.add(next.id);
    current = next;
  }
  return chain;
}

interface LeapRules {
  reach: number;
  touched: Set<string>;
  options: AreaOptions;
  /** The options the sight test runs with - on only when `sightPerLeap` asked. */
  sight: AreaOptions;
}

/** The nearest untouched combatant the arc can reach from `from`, ties to the smaller id, or null. */
function nearestLeap(from: Token, tokens: readonly Token[], rules: LeapRules): Token | null {
  const origin: AreaOrigin = { x: from.x, z: from.z, elevation: from.elevation };
  let best: { token: Token; distance: number } | null = null;
  for (const token of tokens) {
    const distance = leapDistance(origin, token, rules);
    if (distance !== null && nearer(token, distance, best)) best = { token, distance };
  }
  return best?.token ?? null;
}

/** How far a leap to this combatant is, or null when the arc cannot take it. */
function leapDistance(origin: AreaOrigin, token: Token, rules: LeapRules): number | null {
  if (!eligibleToken(token, rules.touched, rules.options)) return null;
  const distance = distanceFrom(origin, token);
  if (distance > rules.reach + bulkOf(token, rules.options.hordeMagnitude)) return null;
  return visibleFromBurst(origin, token, rules.sight) ? distance : null;
}

/** Closer than the best so far, or as close with the smaller id. */
function nearer(token: Token, distance: number, best: { token: Token; distance: number } | null): boolean {
  if (!best || distance < best.distance) return true;
  return distance === best.distance && token.id < best.token.id;
}
